import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { ArrowLeft, Shield, Plus, Copy, Trash2 } from 'lucide-react';

interface Token {
  id: string;
  token: string;
  role: string;
  class: string | null;
  note: string | null;
  created_by: string | null;
  used_by: string | null;
  used_at: string | null;
  expires_at: string;
  created_at: string;
}

const AdminInvitesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [tokens, setTokens] = useState<Token[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'unused' | 'used' | 'expired'>('unused');
  const [role, setRole] = useState<'teacher' | 'admin' | 'class_speaker' | 'parent'>('teacher');
  const [klass, setKlass] = useState('');
  const [note, setNote] = useState('');
  const [ttlHours, setTtlHours] = useState(168);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ tokens: Token[] }>(`/api/admin/invite-tokens?status=${statusFilter}`);
      setTokens(r.tokens);
    } catch (err: any) {
      setMsg(err?.message || 'Fehler');
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== 'admin') return <Navigate to="/dashboard" />;

  const create = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      await api('/api/admin/invite-tokens', {
        method: 'POST',
        body: JSON.stringify({
          role, class: klass || null, note: note || null, ttl_hours: ttlHours,
        }),
      });
      setKlass(''); setNote('');
      setStatusFilter('unused');
      await load();
      setMsg('Token erstellt und an Telegram-Admin-Kanal gepusht.');
    } catch (err: any) { setMsg(err?.message || 'Fehler'); }
    finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('Token widerrufen?')) return;
    try { await api(`/api/admin/invite-tokens/${id}`, { method: 'DELETE' }); await load(); }
    catch (err: any) { setMsg(err?.message || 'Fehler'); }
  };

  const copyLink = async (t: Token) => {
    const appUrl = window.location.origin;
    const url = `${appUrl}/register?invite_token=${encodeURIComponent(t.token)}&role=${encodeURIComponent(t.role)}`;
    await navigator.clipboard.writeText(url);
    setMsg('Link kopiert.');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <Shield className="w-6 h-6 text-pink-400" />
        <h1 className="text-xl font-semibold">Admin · Einladungs-Tokens</h1>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h2 className="font-semibold mb-3">Neuen Token erzeugen</h2>
          <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <select value={role} onChange={e => setRole(e.target.value as any)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm">
              <option value="teacher">Lehrer:in</option>
              <option value="admin">Administrator:in</option>
              <option value="class_speaker">Klassensprecher:in</option>
              <option value="parent">Elternteil</option>
            </select>
            <input type="text" value={klass} onChange={e => setKlass(e.target.value)} placeholder="Klasse (optional)"
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm" />
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Notiz (optional)"
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm sm:col-span-2" />
            <input type="number" min={1} max={720} value={ttlHours}
              onChange={e => setTtlHours(parseInt(e.target.value) || 168)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm" title="Gültigkeit in Stunden" />
            <button type="submit" disabled={busy}
              className="sm:col-span-5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-3 py-2 rounded-lg flex items-center justify-center gap-1">
              <Plus className="w-4 h-4" /> Token erstellen
            </button>
          </form>
        </section>

        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
            <h2 className="font-semibold">Tokens</h2>
            <div className="flex gap-1 bg-gray-900 rounded-lg p-1 text-xs">
              {(['all', 'unused', 'used', 'expired'] as const).map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-2 py-1 rounded ${statusFilter === s ? 'bg-gray-700' : 'text-gray-500'}`}>{s}</button>
              ))}
            </div>
          </div>
          {tokens.length === 0 ? (
            <p className="text-gray-500 text-sm">Keine Tokens in dieser Ansicht.</p>
          ) : (
            <ul className="divide-y divide-gray-700">
              {tokens.map(t => (
                <li key={t.id} className="py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="bg-gray-700 px-2 py-0.5 rounded text-xs">{t.role}</span>
                      {t.class && <span className="bg-gray-700 px-2 py-0.5 rounded text-xs">Klasse {t.class}</span>}
                      {t.used_at && <span className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs">benutzt</span>}
                      {!t.used_at && new Date(t.expires_at) < new Date() && <span className="bg-red-500/20 text-red-300 px-2 py-0.5 rounded text-xs">abgelaufen</span>}
                    </div>
                    <div className="text-xs font-mono mt-1 text-gray-300 break-all">{t.token}</div>
                    {t.note && <div className="text-xs text-gray-400 mt-1">{t.note}</div>}
                    <div className="text-xs text-gray-500 mt-1">
                      erstellt {new Date(t.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                      {' · '}gültig bis {new Date(t.expires_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                      {t.used_at && <> · benutzt {new Date(t.used_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</>}
                    </div>
                  </div>
                  {!t.used_at && new Date(t.expires_at) > new Date() && (
                    <div className="flex gap-2">
                      <button onClick={() => copyLink(t)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg" title="Einladungs-Link kopieren"><Copy className="w-4 h-4" /></button>
                      <button onClick={() => revoke(t.id)} className="p-2 bg-gray-700 hover:bg-red-600 rounded-lg" title="Widerrufen"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {msg && <p className="text-sm text-gray-300">{msg}</p>}
      </main>
    </div>
  );
};

export default AdminInvitesPage;
